<template>
    <ValidationObserver class="popup" ref="form" v-slot="{handleSubmit}">
        <ValidationProvider
            vid="name"
            :name="$t('modals.Landing_name')"
            rules="required|minmax:3,70"
            v-slot="{ errors }"
        >
            <vs-input
                class="w-full"
                :disabled="popupType === 'editLanding'"
                :label="$t('modals.Landing_name')"
                :placeholder="$t('modals.Enter_landing_name')"
                v-model="popupSendData.name"
            />
            <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
        </ValidationProvider>
        <div class="flex mt-4">
            <div class="w-full pr-4">
                <ValidationProvider
                    vid="offer_id"
                    :name="$t('global.Offer')"
                    rules="required"
                    v-slot="{ errors }"
                >
                    <div class="vs-input--label">{{$t('global.Offer')}}</div>
                    <v-select
                        class="w-full popup__select"
                        v-model="popupSendData.offer_id"
                        :options="offersList.options"
                        appendToBody
                        :placeholder="$t('modals.Choose_offer')"
                        :dir="$vs.rtl ? 'rtl' : 'ltr'"
                        :clearable="false"
                        :calculate-position="withPopper"
                    />
                    <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                </ValidationProvider>
            </div>
            <div class="w-full pr-4">
                <ValidationProvider
                    vid="language"
                    :name="$t('global.Language')"
                    rules="required"
                    v-slot="{ errors }"
                >
                    <div class="vs-input--label">{{$t('global.Language')}}</div>
                    <v-select
                        class="w-full popup__select"
                        :placeholder="$t('modals.Choose_language')"
                        v-model="popupSendData.language"
                        :options="language.options"
                        appendToBody
                        :clearable="false"
                        :calculate-position="withPopper"
                    />
                    <p v-for="(error, index) of errors" :key="index" class="text-danger text-xs">{{ error }}</p>
                </ValidationProvider>
            </div>
            <div class="w-full">
                <div class="vs-input--label">{{$t('global.Team')}}</div>
                <v-select
                    class="w-full popup__select"
                    :reduce="a => a.id"
                    :placeholder="$t('modals.Choose_team')"
                    v-model="popupSendData.team_id"
                    :options="teams.options"
                    appendToBody
                    :label="isPermission('settings_team.show_team_name') ? 'name' : 'id'"
                    :calculate-position="withPopper"
                />
            </div>
        </div>

        <div class="mt-4">
            <vs-input
                class="w-full"
                :label="$t('modals.Preview_url')"
                :placeholder="$t('modals.Enter_preview_url')"
                v-model="preview_url"
            />
        </div>

        <div class="mt-4">
            <vs-input
                class="w-full"
                :label="$t('modals.Source_url')"
                :placeholder="$t('modals.Enter_source_url')"
                v-model="source_url"
            />
        </div>
        <vs-row class="mt-5" type="flex">
            <vs-col vs-w="12" class="text-right">
                <vs-button @click="handleSubmit(send)" color="success" type="filled">{{ $t('buttons.Save') }}</vs-button>
            </vs-col>
        </vs-row>
    </ValidationObserver>
</template>

<script>
import vSelect from 'vue-select'
import {ValidationProvider, ValidationObserver} from 'vee-validate'
import ErrorHandler from '../../../utils/errorHandler'
import { mapGetters, mapActions } from 'vuex'
import EventBus from '../../../store/eventBus';
import {vSelectPosition, checkPermission, utils} from "@/utils/mixins";

export default {
    mixins: [vSelectPosition, checkPermission, utils],
    data() {
        return {
            offersList: {
                options: []
            },

            language: {
                options: []
            },

            teams: {
                options: []
            },

            popupSendData: {
                id: this.popupData.id,
                name: '',
                offer_id: '',
                language: '',
                team_id: null
            },

            preview_url: '',
            source_url: '',
        }
    },

    props: {
        popupData: {
            type: Object,
            require: true
        },
        popupType: {
            type: String
        }
    },

    components: {
        ValidationProvider,
        ValidationObserver,
        vSelect
    },

    computed: {
        ...mapGetters(['offers', 'landings', 'languages', 'teamsList']),
    },

    watch: {
        'popupSendData.name'(a, b) {
            const LANDING_PREVIEW_URL = name => `${process.env.VUE_APP_LANDING_PREVIEW_URL}/${name}`
            const LANDING_SOURCE_URL = name => `${process.env.VUE_APP_LANDING_SOURCE_URL}/${name}`

            const PREVIEW_URL_A = LANDING_PREVIEW_URL(a)
            const PREVIEW_URL_B = LANDING_PREVIEW_URL(b)
            const SOURCE_URL_A = LANDING_SOURCE_URL(a)
            const SOURCE_URL_B = LANDING_SOURCE_URL(b)

            if (PREVIEW_URL_B.search(this.preview_url) !== -1) {
                this.preview_url = PREVIEW_URL_A

                if (!a.length) this.preview_url = ''
            }

            if (SOURCE_URL_B.search(this.source_url) !== -1) {
                this.source_url = SOURCE_URL_A

                if (!a.length) this.source_url = ''
            }
        }
    },

    methods: {
        ...mapActions(['GET_OFFER_LIST', 'UPDATE_LANDING', 'CREATE_LANDING']),

        countryName(iso2) {
            for (const language of this.languages) {
                if (language.iso_2 === iso2) {
                    this.popupSendData.language = {label: `${language.language} [${language.iso_2}]`, value: language.iso_2}
                }
            }
        },

        async send() {
            try {
                const data = {
                    ...this.popupSendData,
                    language: this.popupSendData.language.value,
                    offer_id: this.popupSendData.offer_id.value,
                    preview_url: this.preview_url ? this.preview_url.trim() : null,
                    source_url: this.source_url ? this.source_url.trim() : null
                }

                if (this.popupType === 'addLanding'){
                    await this.CREATE_LANDING(data)
                } else {
                    await this.UPDATE_LANDING(data)
                }

                EventBus.$emit('emitPopupEvent', {type: 'close'})
            } catch (error) {
                if(error.response) {
                    const response = error.response.data.result
                    this.$refs.form.setErrors(this.$errors(response))
                }
                new ErrorHandler(error).send
            }
        }
    },

    async mounted() {
        try {
            if (!this.offers.length) {
                await this.GET_OFFER_LIST()
            }

            this.offersList.options = this.offers.map(item => Object.assign({}, {label: item.name, value: item.id}))
            this.teams.options = this.createNameById(this.teamsList)

            if (this.popupType === 'editLanding') {
                for (const lending of this.landings) {
                    if (lending.id === this.popupData.id) {
                        this.popupSendData.name = lending.name
                        const lang = lending.language

                        this.preview_url = lending.preview_url
                        this.source_url = lending.source_url

                        this.countryName(lang.toUpperCase())

                        for (const offer of this.offers) {
                            if (offer.id === lending.offer_id) {
                                this.popupSendData.offer_id = {label: offer.name, value: offer.id}
                                continue
                            }
                        }

                        for (const team of this.teamsList) {
                            if (team.id === lending.team_id) {
                                this.popupSendData.team_id = team.id
                                continue
                            }
                        }
                    }
                }
            }

            for (const language of this.languages) {
                this.language.options.push({ label: `${language.language} [${language.iso_2}]`, value: language.iso_2 })
            }

            this.$store.commit('UPDATE_LOADER_STATE', false)
        } catch (e) {
            this.$store.commit('UPDATE_LOADER_STATE_SET_ERROR', true)
            new ErrorHandler(e).send
        }
    },

}
</script>

<style lang="scss">
.popup {
    margin-top: 0 !important;
    &__select {
        input {
            font-size: 1rem !important;
            color: rgba(0,0,0,.4) !important;
        }
    }
}
</style>
